import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Autocomplete,
  IconButton,
  Alert,
  Divider,
  MenuItem,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
} from "@mui/material";
import {
  Add,
  Delete,
  Search,
  Warning,
  CheckCircle,
  Inventory,
} from "@mui/icons-material";
import { itemService } from "../../services/itemService";
import { departmentService } from "../../services/departmentService";
import { itemIssueService } from "../../services/itemIssueService";
import { useSnackbar } from "notistack";

export const ItemIssueForm = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [items, setItems] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [cart, setCart] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [purpose, setPurpose] = useState("consumption");
  const [requestedBy, setRequestedBy] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [stockValidation, setStockValidation] = useState({});
  const [batchSelection, setBatchSelection] = useState({
    open: false,
    item: null,
    batches: [],
  });

  // Item search states
  const [itemSearchTerm, setItemSearchTerm] = useState("");
  const [itemLoading, setItemLoading] = useState(false);
  const [itemFilters, setItemFilters] = useState({
    inStock: true,
  });

  // Department search state
  const [departmentSearchTerm, setDepartmentSearchTerm] = useState("");
  const [departmentLoading, setDepartmentLoading] = useState(false);

  const [departmentSearchTrigger, setDepartmentSearchTrigger] = useState(0);

  // Initial data loading
  useEffect(() => {
    fetchInitialDepartments();
  }, []);

  // Debounced department search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (departmentSearchTerm.trim() !== "") {
        fetchDepartments();
      } else {
        fetchInitialDepartments();
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [departmentSearchTrigger]);

  // Debounced item search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (itemSearchTerm.trim() !== "") {
        fetchItems();
      } else {
        setItems([]);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [itemSearchTerm, itemFilters]);

  const fetchInitialDepartments = async () => {
    try {
      setDepartmentLoading(true);
      const params = {
        limit: 20,
      };

      const response = await departmentService.getDepartments(params);
      setDepartments(response.data.data || []);
    } catch (error) {
      enqueueSnackbar("Error fetching departments", { variant: "error" });
    } finally {
      setDepartmentLoading(false);
    }
  };

  const fetchItems = async () => {
    try {
      setItemLoading(true);
      const params = {
        search: itemSearchTerm,
        populate: "batches",
        stock: itemFilters.inStock ? "available" : undefined,
        limit: 15,
        page: 1,
      };

      const response = await itemService.getItems(params);
      setItems(response.data.data.items || []);
    } catch (error) {
      enqueueSnackbar("Error fetching items", { variant: "error" });
    } finally {
      setItemLoading(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      setDepartmentLoading(true);
      const params = {
        search: departmentSearchTerm,
        limit: 20,
      };

      const response = await departmentService.getDepartments(params);
      setDepartments(response.data.data.departments || []);
    } catch (error) {
      enqueueSnackbar("Error fetching departments", { variant: "error" });
    } finally {
      setDepartmentLoading(false);
    }
  };

  const validateCartStock = async () => {
    if (cart.length === 0) {
      setStockValidation({});
      return;
    }

    try {
      const itemsToCheck = cart.map((item) => ({
        item: item.item._id,
        quantity: item.quantity,
      }));

      const response = await itemIssueService.checkAvailability({
        items: itemsToCheck,
      });
      const { availability } = response.data.data;

      const validation = {};
      availability.forEach((avail, index) => {
        validation[cart[index].item._id] = {
          sufficient: avail.sufficient,
          available: avail.available,
          warnings: avail.warnings,
        };
      });

      setStockValidation(validation);
    } catch (error) {
      console.error("Stock validation error:", error);
    }
  };

  useEffect(() => {
    validateCartStock();
  }, [cart]);

  const addToCart = (item) => {
    const availableBatches =
      item.batches?.filter((b) => b.availableQuantity > 0) || [];

    if (availableBatches.length === 0) {
      enqueueSnackbar("No available stock for this item", {
        variant: "warning",
      });
      return;
    }

    if (availableBatches.length === 1) {
      addToCartWithBatch(item, availableBatches[0]);
    } else {
      setBatchSelection({
        open: true,
        item,
        batches: availableBatches,
      });
    }
  };

  const addToCartWithBatch = (item, batch) => {
    const existingItem = cart.find(
      (cartItem) =>
        cartItem.item._id === item._id && cartItem.batchStock === batch._id
    );

    if (existingItem) {
      setCart(
        cart.map((cartItem) =>
          cartItem.item._id === item._id && cartItem.batchStock === batch._id
            ? { ...cartItem, quantity: cartItem.quantity + 1 }
            : cartItem
        )
      );
    } else {
      setCart([
        ...cart,
        {
          item,
          quantity: 1,
          costPrice: batch.costPrice,
          batchStock: batch._id,
          batchNumber: batch.batchNumber,
          expiryDate: batch.expiryDate,
        },
      ]);
    }

    enqueueSnackbar(`${item.name} added to cart`, { variant: "success" });
  };

  const removeFromCart = (itemId) => {
    setCart(cart.filter((item) => item.item._id !== itemId));
  };

  const updateQuantity = (itemId, quantity) => {
    if (quantity < 1) {
      removeFromCart(itemId);
      return;
    }

    setCart(
      cart.map((item) =>
        item.item._id === itemId ? { ...item, quantity } : item
      )
    );
  };

  const calculateTotals = () => {
    const subtotal = cart.reduce((sum, item) => {
      return sum + item.quantity * item.costPrice;
    }, 0);

    const tax = subtotal * 0.18;
    const grandTotal = subtotal + tax;
    const totalCost = subtotal;

    return { subtotal, tax, grandTotal, totalCost };
  };

  const handleDepartmentSearchChange = (event, newValue) => {
    setDepartmentSearchTerm(newValue);
    if (newValue !== selectedDepartment?.departmentName) {
      setDepartmentSearchTrigger((prev) => prev + 1);
    }
  };

  const handleDepartmentChange = (event, newValue) => {
    setSelectedDepartment(newValue);
    if (newValue) {
      setDepartmentSearchTerm(newValue.departmentName);
    } else {
      setDepartmentSearchTerm("");
    }
  };

  const handleClearItemSearch = () => {
    setItemSearchTerm("");
    setItems([]);
  };

  const handleSubmit = async () => {
    if (cart.length === 0) {
      enqueueSnackbar("Please add items to cart", { variant: "warning" });
      return;
    }

    if (!selectedDepartment) {
      enqueueSnackbar("Please select a department", { variant: "warning" });
      return;
    }

    const hasStockIssues = Object.values(stockValidation).some(
      (validation) => !validation.sufficient
    );
    if (hasStockIssues) {
      enqueueSnackbar("Some items have insufficient stock", {
        variant: "error",
      });
      return;
    }

    setLoading(true);

    try {
      const nearExpiryItems = cart.filter((item) => {
        const daysToExpiry = Math.ceil(
          (new Date(item.expiryDate) - new Date()) / (1000 * 60 * 60 * 24)
        );
        return daysToExpiry <= 30;
      });

      if (nearExpiryItems.length > 0) {
        enqueueSnackbar(
          `${nearExpiryItems.length} items are near expiry.`,
          { variant: "warning", autoHideDuration: 6000 }
        );
      }

      const issueData = {
        department: selectedDepartment._id,
        items: cart.map((item) => ({
          item: item.item._id,
          batchStock: item.batchStock,
          quantity: item.quantity,
          costPrice: item.costPrice,
        })),
        purpose,
        requestedBy: requestedBy || undefined,
        notes: notes || undefined,
      };

      const response = await itemIssueService.createItemIssue(issueData);

      enqueueSnackbar("Item issue created successfully!", {
        variant: "success",
      });

      const issue = response.data.data.itemIssue;
      enqueueSnackbar(
        `Issue #${issue.issueNumber} created. Status: ${issue.status}`,
        { variant: "info", autoHideDuration: 5000 }
      );

      setCart([]);
      setSelectedDepartment(null);
      setPurpose("consumption");
      setRequestedBy("");
      setNotes("");

      setTimeout(() => {
        navigate("/hospital/hospital-inventory/item-issues");
      }, 2000);
    } catch (error) {
      let errorMessage = "Error creating item issue";

      if (error.response) {
        const backendError = error.response.data;
        errorMessage = backendError.message || errorMessage;

        if (backendError.code === "STOCK_UNAVAILABLE") {
          errorMessage =
            "Insufficient stock. Please update quantities and try again.";
          validateCartStock();
        }
      } else if (error.request) {
        errorMessage = "Network error. Please check your connection.";
      } else {
        errorMessage = error.message || errorMessage;
      }

      enqueueSnackbar(errorMessage, {
        variant: "error",
        autoHideDuration: 5000,
      });

      console.error("Submit error:", error);
    } finally {
      setLoading(false);
    }
  };

  const { subtotal, tax, grandTotal, totalCost } = calculateTotals();

  return (
    <Box>
      <Typography variant="h4" gutterBottom fontWeight="bold">
        New Item Issue
      </Typography>
      <Grid container spacing={3}>
        {/* Left Column - Department & Item Search */}
        <Grid item xs={12} md={8}>
          {/* Department Selection */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                Department Information
              </Typography>

              <Autocomplete
                options={departments}
                getOptionLabel={(option) =>
                  `${option.department} (${option.code})`
                }
                value={selectedDepartment}
                onChange={handleDepartmentChange}
                inputValue={departmentSearchTerm}
                onInputChange={handleDepartmentSearchChange}
                loading={departmentLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search Department"
                    placeholder="Search by name or code"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <Search sx={{ mr: 1, color: "text.secondary" }} />
                          {params.InputProps.startAdornment}
                        </>
                      ),
                      endAdornment: (
                        <>
                          {departmentLoading ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </CardContent>
          </Card>

          {/* Purpose and Requested By */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                Issue Details
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    select
                    label="Purpose"
                    value={purpose}
                    onChange={(e) => setPurpose(e.target.value)}
                  >
                    <MenuItem value="production">Production</MenuItem>
                    <MenuItem value="maintenance">Maintenance</MenuItem>
                    <MenuItem value="consumption">Consumption</MenuItem>
                    <MenuItem value="project">Project</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Requested By (Optional)"
                    value={requestedBy}
                    onChange={(e) => setRequestedBy(e.target.value)}
                    placeholder="Enter requester name"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="Notes (Optional)"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Add any additional notes"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Item Search */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                Add Items
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Search items by name, brand, or category..."
                value={itemSearchTerm}
                onChange={(e) => setItemSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2 }}
              />

              {/* Quick Filters */}
              <Grid container spacing={1} sx={{ mb: 2 }} alignItems="center">
                <Grid item>
                  <Chip
                    label="In Stock Only"
                    color={itemFilters.inStock ? "primary" : "default"}
                    onClick={() =>
                      setItemFilters((prev) => ({
                        ...prev,
                        inStock: !prev.inStock,
                      }))
                    }
                    variant={itemFilters.inStock ? "filled" : "outlined"}
                  />
                </Grid>
                {itemSearchTerm && (
                  <Grid item>
                    <Button
                      size="small"
                      onClick={handleClearItemSearch}
                      sx={{ ml: 1 }}
                    >
                      Clear Search
                    </Button>
                  </Grid>
                )}
              </Grid>

              {/* Item Results */}
              {itemSearchTerm && (
                <Box sx={{ mt: 2, maxHeight: 300, overflow: "auto" }}>
                  {itemLoading ? (
                    <Box
                      sx={{ display: "flex", justifyContent: "center", py: 2 }}
                    >
                      <CircularProgress />
                    </Box>
                  ) : (
                    <>
                      {items.map((item) => {
                        const availableBatches =
                          item.batches?.filter(
                            (b) => b.availableQuantity > 0
                          ) || [];
                        const totalStock = availableBatches.reduce(
                          (sum, batch) => sum + batch.availableQuantity,
                          0
                        );

                        return (
                          <Box
                            key={item._id}
                            sx={{
                              p: 2,
                              mb: 1,
                              border: 1,
                              borderColor: "divider",
                              borderRadius: 1,
                              cursor: "pointer",
                              "&:hover": { bgcolor: "action.hover" },
                            }}
                            onClick={() => addToCart(item)}
                          >
                            <Typography variant="subtitle2" fontWeight="bold">
                              {item.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {item.brand} • {item.itemType} • {item.unit}
                            </Typography>
                            <Typography variant="body2">
                              Category: {item.category}
                            </Typography>
                            <Box sx={{ display: "flex", gap: 1, mt: 0.5 }}>
                              <Chip
                                label={`Stock: ${totalStock}`}
                                size="small"
                                color={totalStock > 0 ? "success" : "error"}
                                variant="outlined"
                              />
                              {availableBatches.length > 1 && (
                                <Chip
                                  label={`${availableBatches.length} batches`}
                                  size="small"
                                  variant="outlined"
                                />
                              )}
                            </Box>
                          </Box>
                        );
                      })}
                      {items.length === 0 && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          textAlign="center"
                          py={2}
                        >
                          No items found
                        </Typography>
                      )}
                    </>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Cart Items */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                Cart Items
              </Typography>

              {cart.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <Inventory
                    sx={{ fontSize: 48, color: "text.secondary", mb: 2 }}
                  />
                  <Typography variant="body1" color="text.secondary">
                    No items in cart. Search and add items above.
                  </Typography>
                </Box>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Item</TableCell>
                        <TableCell align="center">Cost</TableCell>
                        <TableCell align="center">Quantity</TableCell>
                        <TableCell align="right">Amount</TableCell>
                        <TableCell align="center">Stock</TableCell>
                        <TableCell align="center">Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {cart.map((item) => {
                        const validation = stockValidation[item.item._id];
                        const itemAmount = item.quantity * item.costPrice;

                        return (
                          <TableRow key={`${item.item._id}-${item.batchStock}`}>
                            <TableCell>
                              <Box>
                                <Typography
                                  variant="subtitle2"
                                  fontWeight="bold"
                                >
                                  {item.item.name}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {item.item.brand} • {item.item.itemType}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  Batch: {item.batchNumber} • Exp:{" "}
                                  {new Date(
                                    item.expiryDate
                                  ).toLocaleDateString()}
                                </Typography>
                              </Box>
                            </TableCell>

                            <TableCell align="center">
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                ₹{item.costPrice}
                              </Typography>
                            </TableCell>

                            <TableCell align="center">
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  gap: 1,
                                }}
                              >
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    updateQuantity(
                                      item.item._id,
                                      item.quantity - 1
                                    )
                                  }
                                  disabled={item.quantity <= 1}
                                >
                                  -
                                </IconButton>
                                <TextField
                                  size="small"
                                  type="number"
                                  value={item.quantity}
                                  onChange={(e) => {
                                    const newQty = parseInt(e.target.value) || 0;
                                    if (newQty >= 1) {
                                      updateQuantity(item.item._id, newQty);
                                    }
                                  }}
                                  onBlur={(e) => {
                                    // Ensure minimum quantity of 1 on blur
                                    const newQty = parseInt(e.target.value) || 1;
                                    if (newQty < 1) {
                                      updateQuantity(item.item._id, 1);
                                    }
                                  }}
                                  inputProps={{
                                    min: 1,
                                    style: { textAlign: "center" },
                                  }}
                                  sx={{
                                    width: 80,
                                    "& input": {
                                      padding: "8px",
                                      textAlign: "center",
                                    },
                                  }}
                                />
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    updateQuantity(
                                      item.item._id,
                                      item.quantity + 1
                                    )
                                  }
                                >
                                  <Add />
                                </IconButton>
                              </Box>
                            </TableCell>


                            <TableCell align="right">
                              <Typography variant="subtitle1" fontWeight="bold">
                                ₹{itemAmount.toFixed(2)}
                              </Typography>
                            </TableCell>

                            <TableCell align="center">
                              {validation ? (
                                <Chip
                                  icon={
                                    validation.sufficient ? (
                                      <CheckCircle />
                                    ) : (
                                      <Warning />
                                    )
                                  }
                                  label={validation.available}
                                  color={
                                    validation.sufficient ? "success" : "error"
                                  }
                                  size="small"
                                  variant={
                                    validation.sufficient
                                      ? "filled"
                                      : "outlined"
                                  }
                                />
                              ) : (
                                <CircularProgress size={20} />
                              )}
                            </TableCell>

                            <TableCell align="center">
                              <IconButton
                                color="error"
                                onClick={() => removeFromCart(item.item._id)}
                              >
                                <Delete />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column - Order Summary */}
        <Grid item xs={12} md={4}>
          <Card sx={{ position: "sticky", top: 100 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                Issue Summary
              </Typography>

              {selectedDepartment && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Department
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {selectedDepartment.departmentName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Code: {selectedDepartment.departmentCode}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Type: {selectedDepartment.departmentType}
                  </Typography>
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              <Box sx={{ mb: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography variant="body2">Subtotal:</Typography>
                  <Typography variant="body2">
                    ₹{subtotal.toFixed(2)}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography variant="body2">Tax (18%):</Typography>
                  <Typography variant="body2">₹{tax.toFixed(2)}</Typography>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="h6" fontWeight="bold">
                    Total Cost:
                  </Typography>
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    color="primary.main"
                  >
                    ₹{grandTotal.toFixed(2)}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Purpose: {purpose}
                </Typography>
                {requestedBy && (
                  <Typography variant="body2" color="text.secondary">
                    Requested By: {requestedBy}
                  </Typography>
                )}
              </Box>

              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={handleSubmit}
                disabled={cart.length === 0 || !selectedDepartment || loading}
                sx={{ py: 1.5 }}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  `Create Issue - ₹${grandTotal.toFixed(2)}`
                )}
              </Button>

              {Object.values(stockValidation).some((v) => !v.sufficient) && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  Some items have insufficient stock. Please adjust quantities.
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Batch Selection Dialog */}
      <Dialog
        open={batchSelection.open}
        onClose={() =>
          setBatchSelection({ open: false, item: null, batches: [] })
        }
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Select Batch for {batchSelection.item?.name}
        </DialogTitle>
        <DialogContent>
          <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Batch Number</TableCell>
                  <TableCell align="center">Expiry</TableCell>
                  <TableCell align="center">Available</TableCell>
                  <TableCell align="right">Cost Price</TableCell>
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {batchSelection.batches.map((batch) => {
                  const daysToExpiry = Math.ceil(
                    (new Date(batch.expiryDate) - new Date()) /
                    (1000 * 60 * 60 * 24)
                  );
                  return (
                    <TableRow key={batch._id} hover>
                      <TableCell>
                        <Typography variant="subtitle2">
                          {batch.batchNumber}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={new Date(
                            batch.expiryDate
                          ).toLocaleDateString()}
                          color={daysToExpiry <= 30 ? "warning" : "default"}
                          size="small"
                        />
                        {daysToExpiry <= 30 && (
                          <Typography
                            variant="caption"
                            display="block"
                            color="warning.main"
                          >
                            {daysToExpiry} days left
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" fontWeight="bold">
                          {batch.availableQuantity}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="bold">
                          ₹{batch.costPrice}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => {
                            addToCartWithBatch(batchSelection.item, batch);
                            setBatchSelection({
                              open: false,
                              item: null,
                              batches: [],
                            });
                          }}
                        >
                          Select
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
      </Dialog>
    </Box>
  );
};
